import React, { useState, useEffect, useCallback } from "react";

const VIDEO_CHUNK_TYPES = new Set(['video_transcript', 'video_keyframe', 'video_slide']);

/**
 * MultimodalModal — full-screen lightbox for tables, figures, and decision trees.
 *
 * Props:
 *   show        — boolean, controls visibility
 *   onClose     — callback to close the modal
 *   citation    — object with multimodal data:
 *                 { title, chunk_type, visual_type, section_path,
 *                   image_blob_url, enriched_text, snippet,
 *                   authors, journal, specialty, year, doi,
 *                   source_link, pubmed_url, filename }
 */
function MultimodalModal({ show, onClose, citation }) {
  const [rotation, setRotation] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Derive zoom state from level — single source of truth
  const isZoomed = zoomLevel > 1;

  // Reset when citation changes or modal opens
  useEffect(() => {
    if (show) {
      setRotation(0);
      setZoomLevel(1);
    }
  }, [show, citation]);

  // Download: fetch to blob first so `a.download` works for remote URLs
  const handleDownload = useCallback(async () => {
    if (!citation?.image_blob_url) return;
    try {
      const response = await fetch(citation.image_blob_url);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = (citation.title || 'figure').replace(/[^a-z0-9]/gi, '_') + '.' + ext;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(citation.image_blob_url, '_blank');
    }
  }, [citation]);

  // Close on ESC — first press un-zooms, second press closes
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (isZoomed) {
          setZoomLevel(1);
          setRotation(0);
        } else {
          onClose();
        }
      }
    },
    [onClose, isZoomed]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show || !citation) return null;

  const isTable = citation.chunk_type === "table";
  const isFigure =
    citation.chunk_type === "figure_description" ||
    citation.chunk_type === "figure";
  const isVideo = VIDEO_CHUNK_TYPES.has(citation.chunk_type);
  const typeLabel = isTable
    ? "Table"
    : isFigure
    ? "Figure"
    : isVideo
    ? (citation.chunk_type === 'video_transcript' ? 'Transcript'
      : citation.chunk_type === 'video_keyframe' ? 'Keyframe'
      : 'Slide')
    : citation.chunk_type || "Content";
  const typeIcon = isTable ? "📊" : isFigure ? "🖼️" : isVideo ? "🎬" : "📄";

  const sourceHref = isVideo
    ? (citation.video_url || citation.source_link || null)
    : (citation.source_link ||
       citation.pubmed_url ||
       (citation.doi ? `https://doi.org/${citation.doi}` : null));

  /**
   * Render enriched_text as HTML table if markdown, otherwise preformatted.
   */
  const renderTableContent = (text) => {
    if (!text) return null;
    const lines = text.trim().split("\n");
    const isMarkdownTable =
      lines.length >= 2 &&
      lines[0].includes("|") &&
      lines[1].replace(/[|\s:-]/g, "") === "";

    if (isMarkdownTable) {
      const headers = lines[0]
        .split("|")
        .map((h) => h.trim())
        .filter(Boolean);
      const rows = lines.slice(2).map((row) =>
        row
          .split("|")
          .map((c) => c.trim())
          .filter(Boolean)
      );
      return (
        <div className="mm-table-wrapper">
          <table className="mm-rendered-table">
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <pre className="mm-preformatted">{text}</pre>;
  };

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mm-header">
          <div className="mm-header-left">
            <span
              className={`mm-type-badge mm-type-${citation.chunk_type || "text"}`}
            >
              {typeIcon} {typeLabel}
              {citation.visual_type ? ` — ${citation.visual_type}` : ""}
            </span>
            <h3 className="mm-title">
              {citation.title || citation.display_text || "Untitled"}
            </h3>
          </div>
          <button className="mm-close-btn" onClick={onClose} title="Close (ESC)">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Section path breadcrumb */}
        {citation.section_path && (
          <div className="mm-section-path">
            <i
              className="fa-solid fa-sitemap"
              style={{ fontSize: "11px", marginRight: "6px", opacity: 0.5 }}
            ></i>
            {citation.section_path}
          </div>
        )}

        {/* Meta row: authors, journal, year */}
        {(citation.authors || citation.journal || citation.year) && (
          <div className="mm-meta">
            {citation.authors && (
              <span className="mm-meta-item">{citation.authors}</span>
            )}
            {(citation.journal || citation.specialty) && (
              <span className="mm-meta-item">
                {citation.journal || citation.specialty}
              </span>
            )}
            {citation.year && (
              <span className="mm-meta-item">{citation.year}</span>
            )}
          </div>
        )}

        {/* Body */}
        <div className="mm-body">
          {/* Video display */}
          {isVideo && (
            <div className="mm-video-container">
              {(citation.start_time_formatted || citation.end_time_formatted) && (
                <div className="mm-video-timestamp">
                  <i className="fa-solid fa-clock" style={{ marginRight: '6px' }}></i>
                  {citation.start_time_formatted}
                  {citation.end_time_formatted ? ` – ${citation.end_time_formatted}` : ''}
                </div>
              )}
              {/* For keyframes: show the still image first, then link to video */}
              {citation.chunk_type === 'video_keyframe' && citation.image_blob_url && (
                <div className="mm-image-container" style={{ marginBottom: '12px' }}>
                  <img
                    src={citation.image_blob_url}
                    alt={citation.title || 'Keyframe'}
                    className="mm-image"
                    style={{ imageOrientation: 'from-image' }}
                  />
                </div>
              )}
              {citation.video_blob_url ? (
                <video
                  src={citation.video_blob_url}
                  controls
                  className="mm-video-player"
                  preload="metadata"
                />
              ) : citation.video_url ? (
                <div className="mm-video-link-block">
                  <a
                    href={citation.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mm-video-open-btn"
                  >
                    <i className="fa-solid fa-play" style={{ marginRight: '8px' }}></i>
                    Open video at timestamp
                  </a>
                </div>
              ) : null}
              {citation.snippet && (
                <div className="mm-text-content" style={{ marginTop: '16px' }}>
                  <div className="mm-text-label">
                    {citation.chunk_type === 'video_transcript' ? 'Transcript excerpt' : 'Description'}
                  </div>
                  <div className="mm-text-body">{citation.snippet}</div>
                </div>
              )}
            </div>
          )}

          {/* Image display for figures */}
          {!isVideo && citation.image_blob_url && (
            <div className="mm-image-container">
              {/* Toolbar: rotate, zoom, reset, download */}
              <div className="mm-image-toolbar">
                <button
                  className="mm-toolbar-btn"
                  onClick={() => setRotation((r) => (r - 90) % 360)}
                  title="Rotate left"
                >
                  <i className="fa-solid fa-rotate-left"></i>
                </button>
                <button
                  className="mm-toolbar-btn"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  title="Rotate right"
                >
                  <i className="fa-solid fa-rotate-right"></i>
                </button>

                <span className="mm-toolbar-divider" />

                <button
                  className="mm-toolbar-btn"
                  onClick={() => setZoomLevel((z) => Math.max(0.25, z - 0.25))}
                  title="Zoom out"
                  disabled={zoomLevel <= 0.25}
                >
                  <i className="fa-solid fa-magnifying-glass-minus"></i>
                </button>
                <span className="mm-zoom-label">{Math.round(zoomLevel * 100)}%</span>
                <button
                  className="mm-toolbar-btn"
                  onClick={() => setZoomLevel((z) => Math.min(4, z + 0.25))}
                  title="Zoom in"
                  disabled={zoomLevel >= 4}
                >
                  <i className="fa-solid fa-magnifying-glass-plus"></i>
                </button>

                <span className="mm-toolbar-divider" />

                <button
                  className="mm-toolbar-btn"
                  onClick={() => { setRotation(0); setZoomLevel(1); }}
                  title="Reset zoom and rotation"
                >
                  <i className="fa-solid fa-arrows-rotate"></i>
                </button>

                <span className="mm-toolbar-divider" />

                <button
                  className="mm-toolbar-btn"
                  onClick={handleDownload}
                  title="Download image"
                >
                  <i className="fa-solid fa-download"></i>
                </button>
              </div>

              {/*
                Viewport handles overflow/scrollbars.
                Inner zoom-wrapper uses CSS `zoom` so the browser expands
                the element in layout (unlike transform:scale), enabling
                real scrollbars when the image exceeds the viewport.
                Rotation is applied to the img separately so the two
                properties don't interfere.
              */}
              <div className={`mm-image-viewport ${isZoomed ? 'mm-image-viewport-zoomed' : ''}`}>
                <div style={{ zoom: zoomLevel }}>
                  <img
                    src={citation.image_blob_url}
                    alt={citation.title || "Figure"}
                    className="mm-image"
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      imageOrientation: "from-image",
                    }}
                    onClick={() => {
                      if (isZoomed) {
                        setZoomLevel(1);
                        setRotation(0);
                      } else {
                        setZoomLevel(2);
                      }
                    }}
                    title={isZoomed ? "Click to reset zoom and rotation" : "Click to zoom 2×"}
                  />
                </div>
              </div>
              <div className="mm-image-hint">
                <i className={`fa-solid ${isZoomed ? "fa-compress" : "fa-expand"}`}></i>{" "}
                {isZoomed
                  ? "Click image to reset · Scroll to pan"
                  : "Click image to zoom 2× · Use toolbar to rotate"}
              </div>
            </div>
          )}

          {/* Table or enriched text content — not shown for video (handled above) */}
          {!isVideo && (isTable && citation.enriched_text
            ? renderTableContent(citation.enriched_text)
            : (citation.enriched_text || citation.snippet) && (
                <div className="mm-text-content">
                  <div className="mm-text-label">
                    {isTable
                      ? "Table Data"
                      : isFigure
                      ? "Description"
                      : "Excerpt"}
                  </div>
                  <div className="mm-text-body">
                    {citation.enriched_text || citation.snippet}
                  </div>
                </div>
              ))}
        </div>

        {/* Footer: source link */}
        <div className="mm-footer">
          {sourceHref && (
            <a
              href={sourceHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mm-source-link"
            >
              <i className="fa-solid fa-external-link-alt"></i>{" "}
              {isVideo ? "Open video" : citation.doi ? `DOI: ${citation.doi}` : "View Source"}
            </a>
          )}
          <span className="mm-esc-hint">Press ESC to close</span>
        </div>
      </div>
    </div>
  );
}

export default MultimodalModal;
